import React, { useCallback, useEffect, useRef } from 'react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type { ProgressPollingQuery } from '@atlassian/jira-relay/src/__generated__/ProgressPollingQuery.graphql';
import { getAnalyticsMeta } from '../../../common/utils.tsx';
import { useBulkOperationsFormSubmissionActions } from '../../../controllers/use-form-submit-state/main.tsx';
import { ProcessResponse } from './process-response/ProcessResponse.tsx';
import { useAllowedRetries, useRetryInterval } from './utils.tsx';

export const ANALYTICS_ID = 'bulkEditProgressPolling';
export const usePreviousProgress = () => useRef<number | null | undefined>();

export const ProgressPolling = ({
	queryReference,
	refetch,
	taskId,
}: {
	queryReference: PreloadedQuery<ProgressPollingQuery>;
	refetch: (taskId: string) => void;
	taskId: string;
}): React.JSX.Element | null => {
	const { logRetryAttempt, retryStatus, resetRetryCount } = useAllowedRetries();
	const previousProgress = usePreviousProgress();
	const { setError } = useBulkOperationsFormSubmissionActions();

	const data = usePreloadedQuery<ProgressPollingQuery>(
		graphql`
			query ProgressPollingQuery($cloudId: ID!, $taskId: ID!) {
				jira @optIn(to: "JiraIssueBulkOperationProgress") {
					bulkOperationProgress(cloudId: $cloudId, taskId: $taskId) {
						progress
						taskId
						status
						...ProcessResponse
					}
				}
			}
		`,
		queryReference,
	);
	const { status, taskId: dataTaskId, progress } = data.jira?.bulkOperationProgress || {};

	const retry = useCallback(() => {
		if (previousProgress.current !== progress) {
			resetRetryCount();
		} else {
			/*  Log retry attempt as task error when no status/taskId returned.
            Task errors happen because there is a delay before task reaches progress queue.
            Delay P99 is ~1s. */
			logRetryAttempt(!status || !dataTaskId);
		}
		// eslint-disable-next-line react-hooks/react-compiler
		previousProgress.current = progress;
		refetch(dataTaskId || taskId);
	}, [
		progress,
		previousProgress,
		refetch,
		dataTaskId,
		taskId,
		resetRetryCount,
		logRetryAttempt,
		status,
	]);

	const clearRetryInterval = useRetryInterval(taskId, retry);

	useEffect(() => {
		if (retryStatus !== 'underLimit') {
			setError();
			if (retryStatus === 'maxTaskErrorAttemptsReached') {
				fireErrorAnalytics({
					meta: getAnalyticsMeta(ANALYTICS_ID),
					error: new Error('jira.bulkOperationProgress is null or undefined'),
				});
			}
			clearRetryInterval();
		}
	}, [clearRetryInterval, retryStatus, setError]);

	return data.jira?.bulkOperationProgress ? (
		<ProcessResponse
			fragmentRef={data.jira.bulkOperationProgress}
			onStopRetry={clearRetryInterval}
			retryStatus={retryStatus}
		/>
	) : null;
};
